import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { UserInfo } from '@/lib/auth';
import {
  getUserFromOneRoster,
  updateOneRosterUser,
} from '@/lib/auth.oneroster';
import { UserRoles } from '@/lib/constants/auth.constants';
import { handleServerFnError } from '@/lib/sentry-helpers';
import { apiAuthMiddleware } from '@/middlewares/apiAuthMiddleware';
import {
  assertUserHasRole,
  userAuthMiddleware,
} from '@/middlewares/userAuthMiddleware';
import { OneRosterUser } from '@/types/user.oneroster';

export type AuthInfoResponse = {
  isAuthenticated: boolean;
  user: UserInfo | null;
};

export type GetOneRosterUserResponse = {
  user: OneRosterUser | null;
  isAuthenticated: boolean;
};

export const getOneRosterUser = createServerFn()
  .middleware([userAuthMiddleware, apiAuthMiddleware])
  .handler(async ({ context }) => {
    const { cognitoUser, oneRosterApiTyped } = context;

    if (!cognitoUser) {
      return { user: null, isAuthenticated: false } as GetOneRosterUserResponse;
    }

    const user = await getUserFromOneRoster(
      oneRosterApiTyped,
      cognitoUser.email
    );
    return { user, isAuthenticated: true } as GetOneRosterUserResponse;
  });

export const updateUser = createServerFn({ method: 'POST' })
  .middleware([userAuthMiddleware, apiAuthMiddleware])
  .validator(
    z.object({
      user: z.object({
        sourcedId: z.string(),
        username: z.string().nullish(),
        givenName: z.string(),
        familyName: z.string(),
        email: z.string(),
        roles: z.array(
          z.object({
            roleType: z.string(),
            role: z.string(),
            org: z.object({
              sourcedId: z.string(),
            }),
          })
        ),
        metadata: z.record(z.string(), z.any()),
      }),
    })
  )
  .handler(async ({ context, data }) => {
    const { cognitoUser, oneRosterApiTyped } = context;
    const { user } = data;

    if (!cognitoUser) {
      return { success: false, error: 'User not authenticated' };
    }

    await assertUserHasRole(cognitoUser, oneRosterApiTyped, [
      UserRoles.ADMINISTRATOR,
    ]);

    try {
      // Save the updated user
      await updateOneRosterUser(oneRosterApiTyped, user as OneRosterUser);
      const updatedUser = await getUserFromOneRoster(
        oneRosterApiTyped,
        cognitoUser.email
      );
      return {
        success: true,
        user: updatedUser as unknown as OneRosterUser,
      };
    } catch (error) {
      return handleServerFnError(
        error,
        {
          service: 'oneroster-api',
          function: 'updateUser',
          userId: cognitoUser.sub,
        },
        {
          clientMessage: 'Failed to update user',
        }
      );
    }
  });

export const decryptCredential = createServerFn()
  .middleware([userAuthMiddleware, apiAuthMiddleware])
  .validator(
    z.object({
      userId: z.string(),
      credentialId: z.string(),
    })
  )
  .handler(async ({ context, data }) => {
    const { cognitoUser, oneRosterApiTyped } = context;
    const { userId, credentialId } = data;

    if (!cognitoUser) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if user is decrypting their own credential or is an admin
    const oneRosterUser = await getUserFromOneRoster(
      oneRosterApiTyped,
      cognitoUser.email
    );
    const isOwnCredential = oneRosterUser?.sourcedId === userId;

    if (!isOwnCredential) {
      // If not own credential, must be admin
      await assertUserHasRole(cognitoUser, oneRosterApiTyped, [
        UserRoles.ADMINISTRATOR,
      ]);
    }

    try {
      // Call the OneRoster API decrypt endpoint
      const response = await oneRosterApiTyped.POST(
        '/ims/oneroster/rostering/v1p2/users/{userId}/credentials/{credentialId}/decrypt',
        {
          params: {
            path: {
              userId,
              credentialId,
            },
          },
        }
      );

      if (response.error) {
        return {
          success: false,
          error:
            response.error.imsx_description || 'Failed to decrypt credential',
        };
      }

      return {
        success: true,
        password: response.data?.password,
      };
    } catch (error) {
      return handleServerFnError(
        error,
        {
          service: 'oneroster-api',
          function: 'decryptCredential',
          endpoint: `/users/${userId}/credentials/${credentialId}/decrypt`,
          userId: cognitoUser.sub,
        },
        {
          clientMessage: 'Failed to decrypt credential',
        }
      );
    }
  });
